export interface HookDoc {
  name: string
  importLine: string
  signature: string
  notes: string[]
  /** Live example, where one adds something a signature cannot show. */
  example?: string
}

/**
 * Hand-authored, unlike the component props tables. The generator reads
 * `*Props` interfaces; a hook's contract is its call signature, which has no
 * equivalent shape to extract. Signatures here are copied from the shipped
 * declarations and are covered by a test that fails if the export disappears.
 */
export const HOOKS: Record<string, HookDoc> = {
  'use-theme': {
    name: 'useTheme',
    importLine: "import { useTheme } from '@foss/legion-ui'",
    signature: `function useTheme(): ResolvedLegionTheme

interface ResolvedLegionTheme {
  accent: string
  blur: string
  radiusScale: string
  scheme: 'dark' | 'light' | 'system'
  colorScheme: 'dark' | 'light'
  tokens: Readonly<Record<string, string>>
  token: (key: string) => string
  breakpoints: Breakpoints
}`,
    notes: [
      'Returns the resolved theme of the nearest LegionProvider, or the defaults if there is none. This replaces MUI’s useTheme().',
      'Every value is a CSS value string, never a resolved colour. A knob a provider set resolves to that literal; one nobody touched resolves to var(--legion-…), which is still valid in any style prop and keeps the stylesheet the single source of truth.',
      'scheme is what was asked for, including ‘system’. colorScheme is that resolved against prefers-color-scheme, and is what data-theme says.',
    ],
    example: `const theme = useTheme()
return (
  <Stack gap={2} align="start">
    <Text variant="mono">scheme: {theme.scheme}</Text>
    <Text variant="mono">colorScheme: {theme.colorScheme}</Text>
    <Text variant="mono">accent: {theme.accent}</Text>
    <div style={{ width: 48, height: 48, background: theme.accent, borderRadius: 8 }} />
  </Stack>
)`,
  },

  'use-media-query': {
    name: 'useMediaQuery',
    importLine: "import { useMediaQuery } from '@foss/legion-ui'",
    signature: `function useMediaQuery(
  query: string | MediaQueryDescriptor,
  options?: UseMediaQueryOptions
): boolean

type MediaQueryDescriptor =
  | { up: Breakpoint }
  | { down: Breakpoint }
  | { between: [Breakpoint, Breakpoint] }
  | { only: Breakpoint }`,
    notes: [
      'Takes a raw media query string or a breakpoint descriptor. The descriptor form is cheap and pure, so an inline object literal does not thrash the subscription — the string it resolves to is the identity that matters.',
      'noSsr is accepted for MUI parity and ignored; legion always resolves on the first render.',
    ],
    example: `const compact = useMediaQuery({ down: 'md' })
const wide = useMediaQuery('(min-width: 1280px)')
return (
  <Stack gap={2} align="start">
    <Text variant="mono">down(md): {String(compact)}</Text>
    <Text variant="mono">min-width 1280: {String(wide)}</Text>
  </Stack>
)`,
  },

  'use-breakpoint': {
    name: 'useBreakpoint',
    importLine: "import { useBreakpoint } from '@foss/legion-ui'",
    signature: `function useBreakpoint(): Breakpoint

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl'`,
    notes: [
      'The largest breakpoint whose minimum width the viewport has reached. Values match MUI v4: 0 / 600 / 960 / 1280 / 1920.',
    ],
    example: `const key = useBreakpoint()
return <Text variant="mono">breakpoint: {key}</Text>`,
  },

  'use-click-away': {
    name: 'useClickAway',
    importLine: "import { useClickAway } from '@foss/legion-ui'",
    signature: `function useClickAway<T extends HTMLElement>(
  ref: RefObject<T | null>,
  onAway: (event: MouseEvent | TouchEvent) => void,
  enabled?: boolean
): void`,
    notes: [
      'Fires when a pointer press lands outside the referenced element. Used by Menu and Popover to dismiss.',
      'Pass enabled to subscribe only while the surface is open, rather than paying for a document listener that is never useful.',
    ],
  },

  'use-focus-trap': {
    name: 'useFocusTrap',
    importLine: "import { useFocusTrap } from '@foss/legion-ui'",
    signature: `function useFocusTrap<T extends HTMLElement>(
  ref: RefObject<T | null>,
  active: boolean,
  options?: { initialFocus?: RefObject<HTMLElement | null> }
): void`,
    notes: [
      'Keeps Tab and Shift+Tab inside the container while active, and restores focus to whatever had it when the trap is released. Dialog uses it.',
    ],
  },

  'use-escape': {
    name: 'useEscape',
    importLine: "import { useEscape } from '@foss/legion-ui'",
    signature: `function useEscape(
  onEscape: (event: KeyboardEvent) => void,
  enabled?: boolean
): void`,
    notes: [
      'Calls the handler on the Escape key while enabled. Every dismissible overlay in the library uses it, so the behaviour is identical across Dialog, Menu, Popover and Tooltip.',
    ],
  },

  'use-floating': {
    name: 'useFloating',
    importLine:
      "import { useFloating, computePosition, autoUpdate, useOverlayPortal } from '@foss/legion-ui'",
    signature: `function useFloating(options: {
  anchorEl: HTMLElement | null
  placement?: Placement
  offset?: number
  open?: boolean
}): { x: number; y: number; placement: Placement; ref: RefObject<HTMLElement | null> }

type Placement =
  | 'top' | 'top-start' | 'top-end'
  | 'right' | 'right-start' | 'right-end'
  | 'bottom' | 'bottom-start' | 'bottom-end'
  | 'left' | 'left-start' | 'left-end'`,
    notes: [
      'Positions a floating element against an anchor, flipping when it would overflow the viewport. This is the whole of legion’s positioning layer — there is no floating-ui dependency.',
      'computePosition is the one-shot form, autoUpdate keeps a position current across scroll and resize, and useOverlayPortal renders into a portal at the document root so an overlay is not clipped by an ancestor.',
    ],
  },

  cx: {
    name: 'cx',
    importLine: "import { cx } from '@foss/legion-ui'",
    signature: `function cx(...values: ClassValue[]): string

type ClassValue =
  | string
  | number
  | null
  | undefined
  | false
  | Record<string, boolean | null | undefined>
  | ClassValue[]`,
    notes: [
      'Joins class names, skipping anything falsy. Every component uses it to merge a caller’s className with its own, which is why className merges rather than replacing.',
    ],
    example: `const [active, setActive] = useState(true)
const className = cx('base', active && 'is-active', { 'is-wide': true })
return (
  <Stack gap={2} align="start">
    <Text variant="mono">{className}</Text>
    <Button size="sm" onClick={() => setActive(!active)}>Toggle</Button>
  </Stack>
)`,
  },

  space: {
    name: 'Spacing helpers',
    importLine: "import { resolveSpace, resolveSides } from '@foss/legion-ui'",
    signature: `function resolveSpace(value: SpaceValue): string
function resolveSides(value: SpaceProp): string

type SpaceValue = number | string
interface SpaceSides {
  top?: SpaceValue
  right?: SpaceValue
  bottom?: SpaceValue
  left?: SpaceValue
}`,
    notes: [
      'An integer from 0 to 11 is a --legion-space-N step; any other number is pixels; a string is raw CSS. That one rule governs every spacing prop in the library, so gap={3} and gap="1.5rem" both work and mean what they look like.',
    ],
  },

  'breakpoints-api': {
    name: 'breakpoints',
    importLine:
      "import { breakpoints, breakpointKeys, breakpointValues, mediaQuery } from '@foss/legion-ui'",
    signature: `const breakpointValues: Record<Breakpoint, number>
const breakpointKeys: Breakpoint[]
function mediaQuery(query: string | MediaQueryDescriptor): string

interface Breakpoints {
  up(key: Breakpoint): string
  down(key: Breakpoint): string
  between(a: Breakpoint, b: Breakpoint): string
  only(key: Breakpoint): string
  values: Record<Breakpoint, number>
}`,
    notes: [
      'The MUI v4 breakpoint table, unchanged: 0 / 600 / 960 / 1280 / 1920. theme.breakpoints.down(‘xs’) ports across without recalculating anything.',
      'mediaQuery turns a descriptor into the query string, which is what makes an inline descriptor cheap enough to pass on every render.',
    ],
    example: `const theme = useTheme()
return (
  <Stack gap={2} align="start">
    <Text variant="mono">up(md): {theme.breakpoints.up('md')}</Text>
    <Text variant="mono">down(sm): {theme.breakpoints.down('sm')}</Text>
    <Text variant="mono">{JSON.stringify(theme.breakpoints.values)}</Text>
  </Stack>
)`,
  },

  'apply-theme': {
    name: 'applyTheme',
    importLine:
      "import { applyTheme, themeVars, tokenName, resolveScheme, prefersLight, watchScheme } from '@foss/legion-ui'",
    signature: `function applyTheme(el: HTMLElement, theme: Partial<LegionTheme>): void
function themeVars(theme: Partial<LegionTheme>): Record<string, string>
function tokenName(key: string): string
function resolveScheme(scheme: LegionScheme): 'dark' | 'light'
function prefersLight(): boolean
function watchScheme(onChange: (light: boolean) => void): () => void`,
    notes: [
      'The imperative form of what LegionProvider does declaratively: write --legion-* custom properties onto an element. Reach for it outside React, or when theming an element you do not render.',
      'watchScheme returns its own unsubscribe, so it composes with an effect cleanup without a wrapper.',
    ],
  },
}
